
import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from '@/components/ui/phone-input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useFormSyncToHash } from '@/hooks/useFormSyncToHash';

const formSchema = z.object({
  contact_email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  contact_phone: z.string().min(1, "Phone number is required"),
});

type FormValues = z.infer<typeof formSchema>;

interface BusinessContactFormProps {
  formData: any;
  updateFormData: (data: Partial<any>) => void;
  onNext: () => void;
  onPrev: () => void;
}

const BusinessContactForm = ({
  formData,
  updateFormData,
  onNext,
  onPrev
}: BusinessContactFormProps) => {
  const [isPhoneValid, setIsPhoneValid] = React.useState(true);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      contact_email: formData.contact_email || '',
      contact_phone: formData.contact_phone || '',
    },
  });

  // Use the custom hook to sync form data to URL hash
  useFormSyncToHash(form.watch, formData, updateFormData);

  const onSubmit = (values: FormValues) => {
    if (!isPhoneValid) {
      return; // Prevent submission if phone is invalid
    }
    updateFormData(values);
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Contact Information</h2>
        <p className="text-muted-foreground">
          Provide your contact details so our community team can reach you during the review process and for ongoing communication about your business.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="contact_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Email</FormLabel>
                  <FormControl>
                    <Input placeholder="contact@yourbusiness.com" {...field} />
                  </FormControl>
                  <FormDescription>
                    Our team will use this email to contact you about your application and business matters.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="contact_phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={(value) => field.onChange(value || '')}
                      onValidationChange={setIsPhoneValid}
                      defaultCountry="GB"
                      placeholder="Enter phone number"
                    />
                  </FormControl>
                  <FormDescription>
                    A phone number where our team can reach you if needed.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="flex justify-between pt-6">
            <Button type="button" variant="outline" onClick={onPrev}>Previous</Button>
            <Button type="submit" disabled={!isPhoneValid}>Next</Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default BusinessContactForm;
